export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface AutosaveTimers {
  setTimeout: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (handle: ReturnType<typeof setTimeout>) => void;
}

export interface AutosaveSchedulerOptions {
  /** Save once typing has been idle this long. */
  idleMs?: number;
  /** Save at least this often while typing continues. */
  maxWaitMs?: number;
  /** After a failed save, try again after this delay. */
  retryMs?: number;
  timers?: AutosaveTimers;
}

const DEFAULT_IDLE_MS = 2000;
const DEFAULT_MAX_WAIT_MS = 15000;
const DEFAULT_RETRY_MS = 3000;

const defaultTimers: AutosaveTimers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (handle) => clearTimeout(handle),
};

export function isLikelyAuthError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const row = error as {
    status?: unknown;
    code?: unknown;
    name?: unknown;
    message?: unknown;
    statusCode?: unknown;
  };
  if (row.status === 401 || row.statusCode === 401) return true;
  const code = typeof row.code === 'string' ? row.code.toLowerCase() : '';
  if (
    code === 'pgrst301' ||
    code === '401' ||
    code === 'invalid_grant' ||
    code === 'bad_jwt' ||
    code === 'session_not_found'
  ) {
    return true;
  }
  const name = typeof row.name === 'string' ? row.name : '';
  if (name === 'AuthSessionMissingError' || name === 'AuthApiError') {
    const message = typeof row.message === 'string' ? row.message.toLowerCase() : '';
    return (
      message.includes('jwt') ||
      message.includes('expired') ||
      message.includes('session') ||
      name === 'AuthSessionMissingError'
    );
  }
  const message = typeof row.message === 'string' ? row.message.toLowerCase() : '';
  return (
    message.includes('jwt expired') ||
    message.includes('invalid jwt') ||
    message.includes('not authenticated') ||
    message.includes('session expired')
  );
}

/**
 * Run `save`. If it fails with an auth/session error, refresh the session
 * silently and retry once. The editor contents are never cleared here.
 */
export async function saveWithAuthRetry<T>(
  save: () => Promise<T>,
  refreshSession: () => Promise<unknown>,
  isAuthError: (error: unknown) => boolean = isLikelyAuthError,
): Promise<T> {
  try {
    return await save();
  } catch (error) {
    if (!isAuthError(error)) throw error;
    await refreshSession();
    return await save();
  }
}

export interface AutosaveScheduler {
  notifyChange: () => void;
  flush: () => Promise<void>;
  dispose: () => void;
  getStatus: () => AutosaveStatus;
  getLastSavedAt: () => number | null;
  isDirty: () => boolean;
}

/**
 * Debounced autosave: idle delay after typing stops, plus a maximum wait
 * while typing continues. Failures stay dirty and retry without wiping state.
 */
export function createAutosaveScheduler(
  save: () => Promise<void>,
  options: AutosaveSchedulerOptions = {},
): AutosaveScheduler {
  const idleMs = options.idleMs ?? DEFAULT_IDLE_MS;
  const maxWaitMs = options.maxWaitMs ?? DEFAULT_MAX_WAIT_MS;
  const retryMs = options.retryMs ?? DEFAULT_RETRY_MS;
  const timers = options.timers ?? defaultTimers;

  let status: AutosaveStatus = 'idle';
  let lastSavedAt: number | null = null;
  let dirty = false;
  let inFlight = false;
  let disposed = false;
  let idleHandle: ReturnType<typeof setTimeout> | null = null;
  let maxWaitHandle: ReturnType<typeof setTimeout> | null = null;
  let retryHandle: ReturnType<typeof setTimeout> | null = null;

  const clearIdle = () => {
    if (idleHandle != null) {
      timers.clearTimeout(idleHandle);
      idleHandle = null;
    }
  };

  const clearMaxWait = () => {
    if (maxWaitHandle != null) {
      timers.clearTimeout(maxWaitHandle);
      maxWaitHandle = null;
    }
  };

  const clearRetry = () => {
    if (retryHandle != null) {
      timers.clearTimeout(retryHandle);
      retryHandle = null;
    }
  };

  const runSave = async () => {
    if (disposed || inFlight || !dirty) return;
    inFlight = true;
    status = 'saving';
    dirty = false;
    clearIdle();
    clearMaxWait();
    clearRetry();
    try {
      await save();
      if (disposed) return;
      if (!dirty) {
        status = 'saved';
        lastSavedAt = Date.now();
      }
    } catch {
      if (disposed) return;
      dirty = true;
      status = 'error';
      retryHandle = timers.setTimeout(() => {
        retryHandle = null;
        void runSave();
      }, retryMs);
    } finally {
      inFlight = false;
    }
    if (!disposed && dirty && status === 'saving') {
      void runSave();
    }
  };

  const notifyChange = () => {
    if (disposed) return;
    dirty = true;
    if (status !== 'saving') status = 'idle';
    clearIdle();
    clearRetry();
    idleHandle = timers.setTimeout(() => {
      idleHandle = null;
      void runSave();
    }, idleMs);
    if (maxWaitHandle == null) {
      maxWaitHandle = timers.setTimeout(() => {
        maxWaitHandle = null;
        void runSave();
      }, maxWaitMs);
    }
  };

  const flush = async () => {
    if (disposed) return;
    clearIdle();
    clearMaxWait();
    clearRetry();
    if (dirty) await runSave();
  };

  const dispose = () => {
    disposed = true;
    clearIdle();
    clearMaxWait();
    clearRetry();
  };

  return {
    notifyChange,
    flush,
    dispose,
    getStatus: () => status,
    getLastSavedAt: () => lastSavedAt,
    isDirty: () => dirty,
  };
}
